"use client";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import InquiryForm from "@/components/InquiryForm";

interface BookingModalContextValue {
  openModal: () => void;
  closeModal: () => void;
}

const BookingModalContext =
  React.createContext<BookingModalContextValue | null>(null);

export function useBookingModal() {
  const ctx = React.useContext(BookingModalContext);
  if (!ctx)
    throw new Error("useBookingModal must be used within BookingModalProvider");
  return ctx;
}

export function BookingModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const openModal = React.useCallback(() => setIsOpen(true), []);
  const closeModal = React.useCallback(() => setIsOpen(false), []);

  return (
    <BookingModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-x-hidden overflow-y-auto bg-slate-900/60 p-2 sm:p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ type: "spring", duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl min-w-0 flex flex-col bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] mx-0 sm:mx-4 sm:my-8 my-0 sm:rounded-2xl rounded-t-2xl max-h-[calc(100vh-4rem)] overflow-hidden"
            >
              <div className="relative flex flex-col items-center justify-center border-b border-slate-100 px-4 py-3 sm:px-6 sm:py-4 shrink-0">
                <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 text-center pr-10">
                  {t("modal.findCourt")}
                </h2>
                <p className="mt-0.5 text-xs sm:text-sm text-slate-600 text-center">
                  {t("modal.planMatch")}
                </p>
                <button
                  onClick={closeModal}
                  // Place the close button consistently on mobile (near the top) and keep centered placement on larger screens
                  className="absolute right-2 sm:right-4 top-3 sm:top-1/2 sm:-translate-y-1/2 translate-y-0 flex h-10 w-10 sm:h-11 sm:w-11 min-h-10 min-w-10 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label={t("modal.close")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* Make the body scrollable while keeping header fixed. On small screens the modal will act like a sheet. */}
              <div className="min-w-0 px-4 py-4 sm:px-6 sm:pb-6 flex-1 overflow-y-auto">
                <InquiryForm embedded />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </BookingModalContext.Provider>
  );
}
